import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import ApplicationShell from '../../components/application/ApplicationShell';
import ApplicationStepper from '../../components/application/ApplicationStepper';
import { STEP_IDS, STEP_LABELS, stepIndex } from '../../components/application/stepConfig';
import DriverInformationStep from '../../components/application/steps/DriverInformationStep';
import LicenseExperienceStep from '../../components/application/steps/LicenseExperienceStep';
import DrivingRecordStep from '../../components/application/steps/DrivingRecordStep';
import EmploymentStep from '../../components/application/steps/EmploymentStep';
import ReviewSignStep from '../../components/application/steps/ReviewSignStep';
import { useToast } from '../../components/ui/ToastProvider';
import driverApplicationService, { errorMessage } from '../../services/driverApplicationService';
import useLeaveIfNotApplicant from './useLeaveIfNotApplicant';

const STEP_COMPONENTS = {
  personal: DriverInformationStep,
  license: LicenseExperienceStep,
  record: DrivingRecordStep,
  employment: EmploymentStep,
  review: ReviewSignStep
};

const TERMINAL_OR_WAITING = ['submitted', 'approved', 'hired', 'rejected', 'withdrawn', 'expired'];

const pickInitialStep = (application) => {
  const completed = application.progress?.completedSteps || [];
  if (application.status === 'changes_requested' && application.flaggedSteps?.length) {
    return application.flaggedSteps[0];
  }
  const firstIncomplete = STEP_IDS.find((id) => id !== 'review' && !completed.includes(id));
  const saved = application.progress?.currentStep;
  if (saved && STEP_IDS.includes(saved)) {
    // Never land on review while an earlier step is unfinished.
    if (saved === 'review' && firstIncomplete) return firstIncomplete;
    return saved;
  }
  return firstIncomplete || 'review';
};

const ApplyWizardPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const leaveIfNotApplicant = useLeaveIfNotApplicant();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(null);
  const [resumedStep, setResumedStep] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await driverApplicationService.getMine();
        if (cancelled) return;
        if (TERMINAL_OR_WAITING.includes(data.application.status)) {
          navigate('/apply/status', { replace: true });
          return;
        }
        setBundle(data);
        const initial = pickInitialStep(data.application);
        setCurrentStep(initial);
        if (data.application.status === 'in_progress' && data.application.progress?.lastSavedAt) setResumedStep(initial);
      } catch (err) {
        if (cancelled || await leaveIfNotApplicant(err)) return;
        setError(errorMessage(err, 'We could not load your application.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, leaveIfNotApplicant]);

  const application = bundle?.application;
  const flagged = useMemo(() => application?.flaggedSteps || [], [application?.flaggedSteps]);
  const isChangesRequested = application?.status === 'changes_requested';

  const setApplication = useCallback((next) => {
    setBundle((prev) => (prev ? { ...prev, application: { ...prev.application, ...next } } : prev));
  }, []);

  const furthestIndex = useMemo(() => {
    if (!application) return 0;
    if (isChangesRequested) return STEP_IDS.length - 1;
    const completed = application.progress?.completedSteps || [];
    const firstIncomplete = STEP_IDS.findIndex((id) => id !== 'review' && !completed.includes(id));
    return firstIncomplete === -1 ? STEP_IDS.length - 1 : firstIncomplete;
  }, [application, isChangesRequested]);

  const canSelect = useCallback((id) => stepIndex(id) <= furthestIndex, [furthestIndex]);

  const goTo = useCallback((id) => {
    setResumedStep(null);
    if (canSelect(id)) {
      setCurrentStep(id);
      window.scrollTo({ top: 0 });
    }
  }, [canSelect]);

  const handleSaveDraft = useCallback((stepId) => async (payload) => {
    const result = await driverApplicationService.saveStep(application.id, stepId, payload, 'draft');
    setApplication(result.application);
  }, [application?.id, setApplication]);

  const handleComplete = useCallback((stepId) => async (payload) => {
    try {
      const result = await driverApplicationService.saveStep(application.id, stepId, payload, 'complete');
      setApplication(result.application);
      (result.warnings || []).forEach((w) => w.message && toast.info(w.message, { duration: 8000 }));
      const next = isChangesRequested && flagged.length
        ? (flagged[flagged.indexOf(stepId) + 1] || 'review')
        : STEP_IDS[Math.min(stepIndex(stepId) + 1, STEP_IDS.length - 1)];
      setResumedStep(null);
      setCurrentStep(next);
      window.scrollTo({ top: 0 });
      return { ok: true };
    } catch (err) {
      if (err?.status === 400 || err?.status === 403) {
        return { ok: false, errors: err.details || [], message: err.message };
      }
      toast.error(errorMessage(err));
      return { ok: false, message: errorMessage(err) };
    }
  }, [application?.id, setApplication, toast, isChangesRequested, flagged]);

  const handleUpload = useCallback(async (slot, file, onProgress) => {
    const result = await driverApplicationService.uploadDocument(application.id, slot, file, onProgress);
    setApplication({ documents: result.documents });
    toast.success('Uploaded');
  }, [application?.id, setApplication, toast]);

  const handleRemoveDocument = useCallback(async (documentId) => {
    const result = await driverApplicationService.removeDocument(application.id, documentId);
    setApplication({ documents: result.documents });
  }, [application?.id, setApplication]);

  const handleSubmit = useCallback(async (payload) => {
    try {
      await driverApplicationService.submit(application.id, payload);
      toast.success('Application submitted. Thank you!');
      navigate('/apply/status', { replace: true });
      return { ok: true };
    } catch (err) {
      if (err?.status === 400) {
        return { ok: false, errors: err.details || [], message: err.message || 'Some sections still need attention.' };
      }
      return { ok: false, message: errorMessage(err) };
    }
  }, [application?.id, toast, navigate]);

  if (loading) {
    return (
      <ApplicationShell subtitle="Driver Application">
        <div className="flex flex-col items-center py-24 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-brand-mid" aria-hidden="true" />
          <p className="mt-3 text-sm">Loading your application…</p>
        </div>
      </ApplicationShell>
    );
  }

  if (error || !application) {
    return (
      <ApplicationShell subtitle="Driver Application">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error || 'No application found for this account.'}</div>
      </ApplicationShell>
    );
  }

  const StepComponent = STEP_COMPONENTS[currentStep] || DriverInformationStep;
  const locked = isChangesRequested && flagged.length > 0 && currentStep !== 'review' && !flagged.includes(currentStep);
  const flaggedMessage = isChangesRequested && flagged.includes(currentStep) ? application.reviewerMessage : null;
  const index = stepIndex(currentStep);
  const backTarget = index > 0 ? STEP_IDS[index - 1] : null;
  const forwardTarget = index < STEP_IDS.length - 1 ? STEP_IDS[index + 1] : null;

  return (
    <ApplicationShell
      companyName={bundle.company?.name}
      stepper={(
        <ApplicationStepper
          currentId={currentStep}
          completedIds={application.progress?.completedSteps || []}
          flaggedIds={flagged}
          onSelect={goTo}
          canSelect={canSelect}
        />
      )}
    >
      {resumedStep && resumedStep === currentStep && (
        <p className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          Welcome back, you're on step {index + 1} of {STEP_IDS.length}: {STEP_LABELS[currentStep]}.
        </p>
      )}
      <StepComponent
        key={currentStep}
        application={application}
        company={bundle.company}
        legal={bundle.legal}
        stepId={currentStep}
        isFirst={index === 0}
        locked={locked}
        flaggedMessage={flaggedMessage}
        onSaveDraft={handleSaveDraft(currentStep)}
        onComplete={handleComplete(currentStep)}
        onBack={backTarget ? () => goTo(backTarget) : undefined}
        onContinue={forwardTarget ? () => goTo(forwardTarget) : undefined}
        onUpload={handleUpload}
        onRemoveDocument={handleRemoveDocument}
        onEdit={goTo}
        onSubmit={handleSubmit}
      />
    </ApplicationShell>
  );
};

export default ApplyWizardPage;
